import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { formatCount, formatNaira, formatPercent } from '../../utils/money';
import { dateOnly } from '../../utils/datetime';
import EmptyState from './EmptyState';
import { Skeleton } from './Loader';

const LINE = '#2A4459';
const FILL_TOP = 'rgba(42, 68, 89, 0.28)';
const FILL_BOTTOM = 'rgba(42, 68, 89, 0)';
const GRID = 'rgba(42, 68, 89, 0.08)';
const AXIS_TEXT = '#6a869c';

const NO_POINTS = [];

const valueFormatters = {
  currency: formatNaira,
  count: formatCount,
  percent: formatPercent,
};

const areaFill = ({ chart }) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) return FILL_BOTTOM;
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, FILL_TOP);
  gradient.addColorStop(1, FILL_BOTTOM);
  return gradient;
};

const TrendChart = ({ series, loading = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const points = series?.points ?? NO_POINTS;
  const unit = series?.unit;
  const latest = series?.latest;
  const format = valueFormatters[unit] ?? String;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return undefined;

    chartRef.current = new Chart(canvas, {
      type: 'line',
      data: {
        // point.label is a weekday abbreviation that repeats past a 7-day window, so the axis uses point.date.
        labels: points.map((point) => point.date),
        datasets: [
          {
            data: points.map((point) => point.value),
            borderColor: LINE,
            backgroundColor: areaFill,
            borderWidth: 2,
            tension: 0.4,
            fill: true,
            spanGaps: false,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: LINE,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: AXIS_TEXT,
              autoSkip: true,
              maxRotation: 0,
              maxTicksLimit: 7,
              callback(value) {
                return String(this.getLabelForValue(value)).slice(5);
              },
            },
          },
          y: {
            grid: { color: GRID, drawTicks: false },
            border: { display: false },
            ticks: { color: AXIS_TEXT, maxTicksLimit: 4, padding: 8, callback: format },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (items) => items[0].label,
              label: (item) => format(item.parsed.y),
            },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [points, format, loading]);

  if (loading) return <Skeleton h="20rem" />;

  if (points.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center">
        <EmptyState title="No trend data" message="There are no data points in the selected period." />
      </div>
    );
  }

  return (
    <>
      <div className="h-80">
        <canvas ref={canvasRef} role="img" aria-label={`${series?.label ?? 'Trend'} over time`} />
      </div>
      {latest && (
        <p className="mt-3 text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          Latest {dateOnly(latest.date)} · {format(latest.value)}
        </p>
      )}
    </>
  );
};

export default TrendChart;
